import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import api from '../../../services/api';
import { toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';

// P3 (PM analitik tamamlama, 2026-10-08) — E04 Hedef ve KPI Analitiği veri katmanı.
//   GET  v1/performance-goal-analytics/summary   (M10–M14, M61, M62 + tipe göre dağılım)
//   GET  v1/performance-goal-analytics/bands     (gerçekleşme bantları + kurumsal/birim/bireysel)
//   GET  v1/performance-goal-analytics/goals     (hedef listesi; export=xlsx)
//   GET  v1/performance-goal-analytics/goals/{id}          (hedef detayı + plan/gerçekleşen serisi)
//   GET  v1/performance-goal-analytics/goals/{id}/history  (güncelleme geçmişi)
//   GET  v1/performance-goal-analytics/tree      (şirket → birim → bireysel hedef ağacı)
//   GET  v1/performance-goal-analytics/insights  (kural tabanlı AI açıklamaları)
//   POST v1/performance-goal-analytics/goals/{id}/request-update
//   GET  v1/performance-analytics/metrics?codes=M09 (ortak sözlük — ağırlıklı gerçekleşme)
// api.js GET'te reject ETMEZ — hata sinyali res.data.status (false/eksik → panel hata durumu; "boş" ayrı durum).

export const PAGE_SIZE = 10;
export const BANDS = ['lt50', '50_79', '80_99', '100_119', 'gte120', 'noData'];
export const GOAL_STATUSES = ['approved', 'evaluating', 'closed'];
export const RISKS = ['behindPlan', 'staleEvidence'];
export const EVIDENCE = ['fresh', 'stale'];
export const DIRECTIONS = ['increase', 'decrease'];
export const SUMMARY_CODES = ['M10', 'M11', 'M12', 'M13', 'M14', 'M61', 'M62'];

// Çalışan profili (E14, P7a sözleşmesi): /performance/profile/{employeeId}?periodId=…&from=<kaynak yol> — `from`
// /performance/ ile başlıyorsa profil breadcrumb'ında "Geri Dön" olur (süzgeçli E04 adresine dönülür).
export const OWNER_PROFILE_PATH = '/performance/profile';
export const ownerProfileLink = (employeeId, periodId, from) => {
  if (!OWNER_PROFILE_PATH || !employeeId) return null;
  const p = new URLSearchParams();
  if (periodId) p.set('periodId', periodId);
  if (from) p.set('from', from);
  const s = p.toString();
  return `${OWNER_PROFILE_PATH}/${employeeId}${s ? `?${s}` : ''}`;
};

export class PanelError extends Error {}

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

async function get(url, params) {
  const res = await api.get(url, { params });
  const data = payload(res);
  if (data === undefined) throw new PanelError(res?.data?.message || 'error');
  return data;
}

const rows = (data) => (Array.isArray(data) ? data : (data?.data || []));

// ---------------------------------------------------------------- E04 hedef süzgeçleri (URL durumu)
// Kısa URL anahtarları; varsayılanlar URL'ye yazılmaz. F0 anahtarlarıyla (pmAnalyticsFilters: period, dept, loc,
// mgr, sub, title, group, pstatus, org, cmp) ÇAKIŞMAMALI — biri ötekini silerdi (ör. "org" F0'ın orgView'ıdır).
export const GOAL_QUERY_KEYS = Object.freeze({
  types: 'gt', statuses: 'gs', direction: 'dir', risks: 'risk', evidence: 'ev', dueFrom: 'dfrom', dueTo: 'dto',
  owner: 'owner', band: 'band', aligned: 'og', linked: 'linked', overdue: 'late', tab: 'tab',
});
const Q = GOAL_QUERY_KEYS;

const list = (v) => (v ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);

export const EMPTY_GOAL_FILTERS = Object.freeze({
  types: [],
  statuses: [],
  direction: null,
  risks: [],
  evidence: null,
  dueFrom: null,
  dueTo: null,
  ownerEmployeeId: null,
  band: null,
  alignedOrgGoalId: null,
  linked: null,
  overdue: false,
});

export function parseGoalFilters(search) {
  const p = new URLSearchParams(search || '');
  const linked = p.get(Q.linked);
  return {
    types: list(p.get(Q.types)),
    statuses: list(p.get(Q.statuses)).filter((s) => GOAL_STATUSES.includes(s)),
    direction: DIRECTIONS.includes(p.get(Q.direction)) ? p.get(Q.direction) : null,
    risks: list(p.get(Q.risks)).filter((r) => RISKS.includes(r)),
    evidence: EVIDENCE.includes(p.get(Q.evidence)) ? p.get(Q.evidence) : null,
    dueFrom: p.get(Q.dueFrom) || null,
    dueTo: p.get(Q.dueTo) || null,
    ownerEmployeeId: p.get(Q.owner) || null,
    band: BANDS.includes(p.get(Q.band)) ? p.get(Q.band) : null,
    alignedOrgGoalId: p.get(Q.aligned) || null,
    linked: linked === 'true' ? true : linked === 'false' ? false : null,
    overdue: p.get(Q.overdue) === '1',
  };
}

export function serializeGoalFilters(filters, baseSearch = '') {
  const p = new URLSearchParams(baseSearch || '');
  [Q.types, Q.statuses, Q.direction, Q.risks, Q.evidence, Q.dueFrom, Q.dueTo, Q.owner, Q.band, Q.aligned, Q.linked, Q.overdue]
    .forEach((k) => p.delete(k));
  const f = { ...EMPTY_GOAL_FILTERS, ...filters };
  if (f.types.length) p.set(Q.types, f.types.join(','));
  if (f.statuses.length) p.set(Q.statuses, f.statuses.join(','));
  if (f.direction) p.set(Q.direction, f.direction);
  if (f.risks.length) p.set(Q.risks, f.risks.join(','));
  if (f.evidence) p.set(Q.evidence, f.evidence);
  if (f.dueFrom) p.set(Q.dueFrom, f.dueFrom);
  if (f.dueTo) p.set(Q.dueTo, f.dueTo);
  if (f.ownerEmployeeId) p.set(Q.owner, f.ownerEmployeeId);
  if (f.band) p.set(Q.band, f.band);
  if (f.alignedOrgGoalId) p.set(Q.aligned, f.alignedOrgGoalId);
  if (f.linked != null) p.set(Q.linked, String(f.linked));
  if (f.overdue) p.set(Q.overdue, '1');
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * E04 uç parametreleri. `withListKeys` false iken liste-özel süzgeçler (bant, kurumsal hedef, bağ) gönderilmez:
 * kartlar ve bant dağılımı bant süzgecinden bağımsızdır (dağılımın kendisi), liste ise süzülür.
 */
export function toGoalParams(goalFilters, { withListKeys = false, withBand = false } = {}) {
  const f = { ...EMPTY_GOAL_FILTERS, ...goalFilters };
  const params = {};
  if (f.types.length) params['goalType[in]'] = f.types.join(',');
  if (f.statuses.length) params['goalStatus[in]'] = f.statuses.join(',');
  if (f.direction) params['direction[eq]'] = f.direction;
  if (f.risks.length) params['risk[in]'] = f.risks.join(',');
  if (f.evidence) params['evidence[eq]'] = f.evidence;
  if (f.dueFrom) params['dueAt[gte]'] = f.dueFrom;
  if (f.dueTo) params['dueAt[lte]'] = f.dueTo;
  if (f.ownerEmployeeId) params['ownerEmployeeId[eq]'] = f.ownerEmployeeId;
  if (f.overdue) params['overdue[eq]'] = 'true';
  if (withListKeys) {
    if (f.alignedOrgGoalId) params['alignedOrgGoalId[eq]'] = f.alignedOrgGoalId;
    if (f.linked != null) params['linked[eq]'] = String(f.linked);
  }
  if (withBand && f.band) params['band[eq]'] = f.band;
  return params;
}

/** Etkin (varsayılandan farklı) hedef süzgeçleri — silinebilir etiketler. */
export function activeGoalFilterKeys(goalFilters) {
  const f = { ...EMPTY_GOAL_FILTERS, ...goalFilters };
  return Object.keys(EMPTY_GOAL_FILTERS).filter((k) => (Array.isArray(f[k]) ? f[k].length > 0
    : typeof f[k] === 'boolean' && k === 'overdue' ? f[k] : f[k] != null));
}

export function clearGoalFilter(goalFilters, key) {
  return { ...EMPTY_GOAL_FILTERS, ...goalFilters, [key]: EMPTY_GOAL_FILTERS[key] };
}

// Yazımlar o ANKİ adresten (history.location) okunur, render anındaki kapanıştan değil: aynı olay içinde art arda
// iki güncelleme (ör. ağaç düğümü → süzgeç + sekme) birbirini ezmez.
export function useGoalFilters() {
  const location = useLocation();
  const history = useHistory();
  const goalFilters = useMemo(() => parseGoalFilters(location.search), [location.search]);
  const setGoalFilters = useCallback((next) => {
    const current = history.location;
    const value = typeof next === 'function' ? next(parseGoalFilters(current.search)) : next;
    history.replace({ pathname: current.pathname, search: serializeGoalFilters(value, current.search) });
  }, [history]);
  const resetGoalFilters = useCallback(() => setGoalFilters({ ...EMPTY_GOAL_FILTERS }), [setGoalFilters]);
  return { goalFilters, setGoalFilters, resetGoalFilters };
}

// Sekme (overview | links) de URL'de — geri dönüşte korunur.
export function useTabParam() {
  const location = useLocation();
  const history = useHistory();
  const tab = new URLSearchParams(location.search).get(Q.tab) === 'links' ? 'links' : 'overview';
  const setTab = useCallback((next) => {
    const current = history.location;
    const p = new URLSearchParams(current.search);
    if (next === 'overview') p.delete(Q.tab); else p.set(Q.tab, next);
    const s = p.toString();
    history.replace({ pathname: current.pathname, search: s ? `?${s}` : '' });
  }, [history]);
  return { tab, setTab };
}

// ---------------------------------------------------------------- uçlar
// Kartlar ve AI açıklamaları bant dahil tüm hedef süzgeçlerini izler (bant tıklaması koşul ekler);
// bant dağılımının kendisi bant süzgecinden bağımsızdır.
export const fetchSummary = (filters, goalFilters) => get('/v1/performance-goal-analytics/summary', {
  ...toAnalyticsParams(filters), ...toGoalParams(goalFilters, { withListKeys: true, withBand: true }),
});

export const fetchM09 = async (filters) => {
  const data = await get('/v1/performance-analytics/metrics', { ...toAnalyticsParams(filters), codes: 'M09' });
  return (data || []).find((m) => m.metricCode === 'M09') || null;
};

export const fetchBands = (filters, goalFilters) => get('/v1/performance-goal-analytics/bands', {
  ...toAnalyticsParams(filters), ...toGoalParams(goalFilters, { withListKeys: true }),
});

export const fetchGoals = async (filters, goalFilters, {
  pageNumber = 1, pageSize = PAGE_SIZE, sort, order,
} = {}) => {
  const data = await get('/v1/performance-goal-analytics/goals', {
    ...toAnalyticsParams(filters), ...toGoalParams(goalFilters, { withListKeys: true, withBand: true }),
    ...(sort ? { sort, order: order || 'asc' } : {}),
    pageNumber, pageSize,
  });
  return {
    rows: rows(data),
    total: Array.isArray(data) ? data.length : (data?.totalRowCount ?? rows(data).length),
    pageIndex: data?.pageIndex ? data.pageIndex - 1 : 0,
    pageSize: data?.pageSize || pageSize,
  };
};

export const exportGoals = (filters, goalFilters, { sort, order } = {}) => api.get('/v1/performance-goal-analytics/goals', {
  params: {
    ...toAnalyticsParams(filters), ...toGoalParams(goalFilters, { withListKeys: true, withBand: true }),
    ...(sort ? { sort, order: order || 'asc' } : {}),
    export: 'xlsx',
  },
  responseType: 'blob',
});

// Detay/geçmiş/ağaç yalnız F0 kapsamını taşır (hedef süzgeci yok — backend izin listesi).
export const fetchGoalDetail = (filters, goalId) => get(`/v1/performance-goal-analytics/goals/${goalId}`, toAnalyticsParams(filters));

export const fetchGoalHistory = async (filters, goalId, { pageNumber = 1, pageSize = 20 } = {}) => {
  const data = await get(`/v1/performance-goal-analytics/goals/${goalId}/history`, {
    ...toAnalyticsParams(filters), pageNumber, pageSize,
  });
  return { rows: rows(data), total: data?.totalRowCount ?? rows(data).length };
};

export const fetchTree = (filters) => get('/v1/performance-goal-analytics/tree', toAnalyticsParams(filters));

export const fetchInsights = (filters, goalFilters) => get('/v1/performance-goal-analytics/insights', {
  ...toAnalyticsParams(filters), ...toGoalParams(goalFilters, { withListKeys: true, withBand: true }),
});

// Mutasyon: interceptor başarı mesajını (performance.goalAnalytics.updateRequested) ve hatayı kendisi tostlar.
export const requestGoalUpdate = async (filters, goalId) => {
  try {
    await api.post(`/v1/performance-goal-analytics/goals/${goalId}/request-update`, null, {
      params: { 'periodId[eq]': filters.periodId },
    });
    return true;
  } catch {
    return false;
  }
};

export const fetchPeriods = async () => {
  const res = await api.get('/v1/evaluation-periods', { params: { pageSize: 200, orderBy: 'startDate', orderDir: 'desc' } });
  return rows(payload(res) || []);
};

// Excel indirme: başarısız GET'te interceptor hatayı tostlar ve başlıksız {status:false} döner — sessizce çıkılır.
export function downloadBlob(res, fallbackName) {
  if (!res?.headers || res.data?.status === false) return false;
  const blob = new Blob([res.data], { type: res.headers['content-type'] });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
  a.download = match ? match[1].replaceAll('"', '') : fallbackName;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}

// ---------------------------------------------------------------- gösterim yardımcıları
export const metricOf = (list, code) => (list || []).find((m) => m.metricCode === code) || null;

// Payda 0 ya da değer yok → "Veri Yok" (analiz §20); gizlenmiş hücre ayrı durum.
export const hasValue = (m) => !!m && m.value != null && !(m.denominator != null && Number(m.denominator) === 0);
export const isSuppressed = (m) => !!m?.suppressionReason;

export const formatNumber = (v, lang, digits = 2) => (v == null ? '' : new Intl.NumberFormat(lang, { maximumFractionDigits: digits }).format(Number(v)));

export const dayOf = (v) => (v ? String(v).slice(0, 10) : '');

/** Bant çubukları: gizlenmiş bant değer taşımaz (grafikte 0 çizilmez, tabloda "Gizlendi"). */
export function bandRows(bands, t) {
  return BANDS.map((key) => {
    const b = (bands || []).find((x) => x.key === key) || { key };
    return {
      key,
      label: t(`performance.goalAnalytics.band.${key}`),
      goals: b.goals ?? null,
      persons: b.persons ?? null,
      suppressed: !!b.suppressionReason,
    };
  });
}

/**
 * Zaman serisi: plan ve gerçekleşen noktaları tarih ekseninde birleştirir. Bir seride o tarihte veri yoksa
 * değer null kalır ve çizgi KESİLİR (connectNulls=false — analiz: "veri noktası olmayan tarihte çizgi kesilir").
 */
export function mergeSeries(actual, plan) {
  const byDay = new Map();
  (plan || []).forEach((p) => {
    const day = dayOf(p.at);
    byDay.set(day, { day, plan: p.value ?? null, actual: byDay.get(day)?.actual ?? null });
  });
  (actual || []).forEach((a) => {
    const day = dayOf(a.at);
    const prev = byDay.get(day) || { day, plan: null, actual: null };
    byDay.set(day, { ...prev, actual: a.value ?? null });
  });
  return [...byDay.values()].sort((x, y) => x.day.localeCompare(y.day));
}

/** Gerçekleşme hücresi metni: ham r ve (farklıysa) puanlamaya giren r′. */
export function completionText(row, lang) {
  if (row?.rawPct == null) return null;
  const raw = `${formatNumber(row.rawPct, lang)}%`;
  if (row.scoredPct == null || Number(row.scoredPct) === Number(row.rawPct)) return raw;
  return `${raw} → ${formatNumber(row.scoredPct, lang)}%`;
}
